import React, { Key, useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { $Tabs } from "us.common/components";
import { searchValues } from "components/TemplateEditor/Functions";
import * as Actions from "actions";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { TabContent } from "./Components";
import { ISectionRule, ITemplate } from "interfaces";
import {
  getSectionByName,
  getSectionRuleByRule,
  updateTemplateSectionRule,
} from "functions";
import { getUrlParams } from "components/EditorHome/Functions";
import { useLocation } from "react-router-dom";

interface IParameters {
  onDoubleClick?: (
    tokenName: string,
    isRepeating: boolean,
    adapterId?: number
  ) => void;
  onAddRepeatingAdapter?: () => void;
  showAllRepeatingAdapters?: boolean;
  enableRepeatingTab?: boolean;
  disabledAddRepeatingAdapter?: boolean;
  isDisplayInTextEditor?: boolean;
}

const TabType = {
  STANDARD: "standard",
  REPEATING: "repeating",
};

/**
 * @description - Parameters and repeating parameters component.
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 11/09/2022
 */
const Parameters: React.FC<IParameters & PropsFromRedux> = (props) => {
  const {
    section,
    template,
    parameters,
    showAllRepeatingAdapters = true,
    enableRepeatingTab = true,
    disabledAddRepeatingAdapter,
    isDisplayInTextEditor,
    standardAdapters,
    repeatingAdapters,
    onDoubleClick,
    selectSection,
    updateTemplate,
    onAddRepeatingAdapter,
    getParameters,
    getAdaptersIds,
  } = props;

  const { t } = useTranslation();
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState(TabType.STANDARD);
  const { adapterId } = standardAdapters?.data[0] ?? {};

  useEffect(() => {
    if (isDisplayInTextEditor && adapterId != null) {
      getParameters && getParameters([adapterId]);
    } else {
      if (template.data && !template.loading) {
        const { templateId } = template.data;
        if (
          templateId == 0 &&
          repeatingAdapters.data.length > 0 &&
          !repeatingAdapters.data[0]?.isRepeating
        ) {
          const updatedTemplate: ITemplate = {
            ...template.data,
            mainAdapterId: repeatingAdapters.data[0].adapterId,
            templateContent: {
              ...template.data.templateContent,
              adapter: {
                ...template.data.templateContent.adapter,
                mainAdapterId: repeatingAdapters.data[0].adapterId,
              },
            },
          };
          updateTemplate && updateTemplate(updatedTemplate);
          getParameters && getParameters([repeatingAdapters.data[0].adapterId]);
        }
      }
    }
  }, [repeatingAdapters]);

  useEffect(() => {
    if (section) {
      setActiveTab(section.repeatable ? TabType.REPEATING : TabType.STANDARD);
    }
  }, [section]);

  /**
   * @description - Handle repeating adapter change for the repeatable section
   * @param {any} adapterId - Selected repeating adapter id
   */
  const handleAdapterChange = (adapterId: Key) => {
    // update the section with new adapter id
    selectSection &&
      selectSection({
        ...section,
        repeatable: true,
        repeatingAdapterId: adapterId,
      });
    const oldSection = getSectionByName(
      section.name,
      template.data.templateContent.pages
    );
    if (oldSection) {
      const oldSectionRule = getSectionRuleByRule(
        section.selectedRule,
        oldSection
      );
      if (oldSectionRule) {
        const updatedSectionRule: ISectionRule = {
          ...oldSectionRule,
          isRepeating: true,
          repeatingAdapterId: parseInt(adapterId.toString()),
        };

        const updatedTemplate = updateTemplateSectionRule(
          section.name,
          section.selectedRule,
          updatedSectionRule,
          template.data
        );
        updateTemplate && updateTemplate(updatedTemplate);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        searchText: "",
      }}
      onSubmit={() => {}}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
    >
      {({ values }: any) => (
        <div className="pb-3">
          <div className="d-flex justify-content-between align-items-center px-2 py-2 panel-title">
            <h3 className="py-1 m-0">
              {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PARAMETERS")}
            </h3>
          </div>

          {/* commented due to [antd: Tabs] Tabs.TabPane is deprecated */}
          {/* <$Tabs
            defaultActiveKey={TabType.STANDARD}
            activeKey={activeTab}
            onTabClick={(key: string) => setActiveTab(key)}
          >
            <$Tabs.TabPane
              tab={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.STANDARD")}
              key={TabType.STANDARD}
            >
              <TabContent
                data={searchValues(
                  parameters.standard.dataTokens,
                  values.searchText,
                  "tokenName"
                )}
                adapterName={parameters.standard.adapterName}
                isLoading={parameters.isLoading}
                onSelectToken={onDoubleClick}
              />
            </$Tabs.TabPane>
            {enableRepeatingTab && (
              <$Tabs.TabPane
                tab={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REPEATING")}
                key={TabType.REPEATING}
              >
                <TabContent
                  data={
                    showAllRepeatingAdapters
                      ? parameters.repeating
                      : parameters.repeating.filter(
                          (adapter) =>
                            adapter.adapterId == section.repeatingAdapterId
                        )
                  }
                  isLoading={parameters.isLoading}
                  isRepeating
                  disabledAddRepeatingAdapter={disabledAddRepeatingAdapter}
                  onSelectToken={onDoubleClick}
                  onSelectAdapter={handleAdapterChange}
                  onClickAddRepeatingAdapter={onAddRepeatingAdapter}
                  disabled={!template?.data}
                />
              </$Tabs.TabPane>
            )}
          </$Tabs> */}
            <$Tabs
            defaultActiveKey={TabType.STANDARD}
            activeKey={activeTab}
            onTabClick={(key: string) => setActiveTab(key)}
            items={[
              {
                label: t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.STANDARD"),
                key: TabType.STANDARD,
                children: (
                  <TabContent
                    data={searchValues(
                      parameters.standard.dataTokens,
                      values.searchText,
                      "tokenName"
                    )}
                    adapterName={parameters.standard.adapterName}
                    isLoading={parameters.isLoading}
                    onSelectToken={onDoubleClick}
                  />
                ),
              },
              enableRepeatingTab
                ? {
                    label: t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REPEATING"),
                    key: TabType.REPEATING,
                    children: (
                      <TabContent
                        data={
                          showAllRepeatingAdapters
                            ? parameters.repeating
                            : parameters.repeating.filter(
                                (adapter) => adapter.adapterId == section.repeatingAdapterId
                              )
                        }
                        isLoading={parameters.isLoading}
                        isRepeating
                        disabledAddRepeatingAdapter={disabledAddRepeatingAdapter}
                        onSelectToken={onDoubleClick}
                        onSelectAdapter={handleAdapterChange}
                        onClickAddRepeatingAdapter={onAddRepeatingAdapter}
                        disabled={!template?.data}
                      />
                    ),
                  }
                : null,
            ].filter((tab): tab is Exclude<typeof tab, null> => tab !== null)} // Filter out null values
          />
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory, textEditor } = state;
  const { parameters, section, repeatingAdapters } = templateEditor;
  const { template } = templatesHistory;
  const { standardAdapters } = textEditor;
  return {
    parameters,
    section,
    template,
    repeatingAdapters,
    standardAdapters,
  };
};

const { section, parameters, repeatingAdapters } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  selectSection: section.selectSection,
  updateTemplate: template.update,
  getParameters: parameters.get,
  getAdaptersIds: repeatingAdapters.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Parameters);
